/**
 * What KIND of picture is this, and therefore how should a grid show it?
 *
 * A thumbnail's fit is a property of the asset, not of the grid. The image
 * picker's corpus is logo-heavy — the local index is 8,823 entries with zero
 * SVGs, 849 PNGs, and marks 4:1 and wider — so a blanket `object-fit: cover`
 * crops a wordmark to its middle third and upscales it past natural size.
 * This module is the decision; `poster.fit` / `poster.background` (MK-2a) is
 * how the decision reaches the DOM.
 *
 * Pure: no DOM, no fetch, no state. Every input is optional and every one of
 * them may be wrong or absent — the corpus index, for one, carries widths but
 * **not a single height** (15,454 vs 0), so aspect ratio is unknown for most
 * entries until the browser has decoded the image. The classifier must return
 * a usable answer anyway.
 *
 * THE BIAS IS DELIBERATE AND ASYMMETRIC. When the kind cannot be determined,
 * `contain` wins. A letterboxed photograph is a blemish; a cropped logo is a
 * bug. Do not "fix" `unknown` to `cover` because most of the corpus is
 * photographs — most is not the same as this one.
 */

/**
 * Formats whose bytes can carry transparency. The checkerboard is INFORMATION
 * — it says "this asset has holes in it" — so drawing it behind a format that
 * cannot have any states something untrue.
 *
 * `gif` is here and is not in the original plan's list: GIF carries binary
 * (1-bit) transparency, and one entry in the corpus is a GIF. `jpg`/`jpeg` is
 * the load-bearing exclusion — JPEG has no alpha channel at all, which is why
 * rule 4 below can answer "photo" from the format alone.
 */
export const ALPHA_CAPABLE_FORMATS = new Set(['png', 'webp', 'avif', 'gif', 'svg', 'apng', 'ico']);

/** Both dimensions at or below this and it is an icon, not a picture. */
export const ICON_MAX_EDGE = 160;

/**
 * Aspect band a photograph plausibly occupies. Outside it — wider than 2:1 or
 * taller than 1:2 — an alpha-capable asset is a banner, a wordmark or a strip,
 * none of which survives a centre crop.
 */
export const PHOTO_ASPECT_MIN = 0.5;
export const PHOTO_ASPECT_MAX = 2.0;

const EXTENSION_RE = /\.([a-z0-9]{2,5})(?:$|[?#])/i;
const DATA_URI_RE = /^data:image\/([a-z0-9.+-]+)[;,]/i;

/**
 * Resolve a lowercase format token from whatever the caller happens to hold.
 *
 * The `url` path is the least trustworthy of the three and is tried last, for
 * a reason MK-0's harness fixture found the hard way: a percent-encoded SVG
 * data URI whose payload mentions `w3.org` yields `.org%2f2000%2fsvg…` from
 * `new URL(url).pathname`, which is not an extension and matches no list.
 * Data URIs are therefore read from their MIME type, never from a path.
 *
 * @param {{format?: string, filename?: string, url?: string}} input
 * @returns {string} lowercase token, or '' when nothing is derivable
 */
export function normalizeMediaFormat({ format, filename, url } = {}) {
  const explicit = String(format ?? '')
    .trim()
    .toLowerCase()
    .replace(/^image\//, '');
  if (explicit) return canonicalFormat(explicit);

  const fromName = EXTENSION_RE.exec(String(filename ?? ''));
  if (fromName) return canonicalFormat(fromName[1].toLowerCase());

  const raw = String(url ?? '');
  const dataUri = DATA_URI_RE.exec(raw);
  if (dataUri) return canonicalFormat(dataUri[1].toLowerCase());

  // Strip a query/hash before looking for an extension so `logo.png?v=2`
  // resolves, then take the LAST match so a path segment cannot win over the
  // filename.
  const path = raw.split(/[?#]/, 1)[0];
  const segment = path.slice(path.lastIndexOf('/') + 1);
  const fromUrl = EXTENSION_RE.exec(segment);
  return fromUrl ? canonicalFormat(fromUrl[1].toLowerCase()) : '';
}

function canonicalFormat(token) {
  if (token === 'jpeg') return 'jpg';
  if (token === 'svg+xml') return 'svg';
  if (token === 'x-icon' || token === 'vnd.microsoft.icon') return 'ico';
  if (token === 'tif') return 'tiff';
  return token;
}

function finiteEdge(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * @typedef {object} MediaClassification
 * @property {'photo'|'graphic'|'icon'|'unknown'} kind
 * @property {'cover'|'contain'} fit
 * @property {boolean} checker — show the transparency checkerboard behind it
 */

/**
 * @param {{format?: string, filename?: string, url?: string,
 *   naturalWidth?: number, naturalHeight?: number, hasAlpha?: boolean}} [input]
 *   `hasAlpha` is tri-state: `true`/`false` from a decoded bitmap probe, and
 *   `undefined` when nobody has looked. Undefined falls back to what the
 *   FORMAT can carry, which is a weaker claim and is treated as one.
 * @returns {MediaClassification}
 */
export function classifyMedia(input = {}) {
  const format = normalizeMediaFormat(input);
  const alphaCapable = ALPHA_CAPABLE_FORMATS.has(format);
  const width = finiteEdge(input.naturalWidth);
  const height = finiteEdge(input.naturalHeight);
  const aspect = width && height ? width / height : null;

  const kind = classifyKind({ format, alphaCapable, width, height, aspect });

  // A probe beats the format. `hasAlpha === false` on a PNG is a real answer —
  // most PNGs in a crawled corpus are opaque screenshots — and a checkerboard
  // behind an opaque asset is a lie about the asset.
  const transparent = input.hasAlpha === undefined ? alphaCapable : !!input.hasAlpha;

  return {
    kind,
    // Only `photo` earns a crop. Everything else — including `unknown` — is
    // shown whole. See the header: the bias is not symmetric.
    fit: kind === 'photo' ? 'cover' : 'contain',
    // `unknown` gets no checkerboard either: we do not know that it has holes,
    // and the checker is a claim, not a decoration.
    checker: transparent && (kind === 'graphic' || kind === 'icon')
  };
}

/** The ordered rules. Order is the contract; the first match wins. */
function classifyKind({ format, alphaCapable, width, height, aspect }) {
  // 1. Vector is a graphic by definition, at any size or aspect.
  if (format === 'svg') return 'graphic';

  // 2. Small in BOTH directions. Needs both edges — a 48px-wide entry with no
  //    height could be a 48x2000 sidebar strip.
  if (width && height && width <= ICON_MAX_EDGE && height <= ICON_MAX_EDGE) return 'icon';

  // 3. Alpha-capable AND outside the band a photograph occupies. Both halves
  //    are required: a 3:1 JPEG is a panorama, and cropping it is fine.
  if (alphaCapable && aspect !== null && (aspect < PHOTO_ASPECT_MIN || aspect > PHOTO_ASPECT_MAX)) {
    return 'graphic';
  }

  // 4. JPEG cannot carry alpha, so it is not a logo, a badge or an overlay.
  //    This is the one rule that answers from the format alone, and it covers
  //    7,234 of the corpus's 8,823 entries.
  if (format === 'jpg') return 'photo';

  // 5. An alpha-capable format with no usable dimensions, or a format nobody
  //    recognises. Both are genuinely unknown and both take `contain`.
  return 'unknown';
}
